/**
 * Eraya price-test theme bridge — Symmetry (Clean Canvas).
 *
 * The PriceTestEngine only scans at init and when its public methods are called.
 * Symmetry updates prices in three dynamic ways that must be re-synced:
 *
 *   1. Variant change. `variant-picker.js` fetches the section and swaps every
 *      [data-dynamic-variant-content] node — including the price — for freshly
 *      rendered HTML holding the ORIGINAL price. It then dispatches
 *      `on:variant:change` 10ms later, i.e. AFTER paint. Relying on that event
 *      alone flashes the base price, so the MutationObserver re-applies
 *      synchronously (microtask, before paint) and the event is belt-and-braces.
 *   2. AJAX product cards. Collection filtering/pagination, predictive search,
 *      quick buy, cart drawer recommendations and shop-the-look all inject
 *      <product-block> elements. A debounced rescan is fine there.
 *   3. Cart re-renders (`updateFromCartChange`) replace cart line items.
 *
 * Also intercepts Buy It Now / accelerated checkout so a tested product is
 * actually CHARGED the test price (those buttons bypass /cart/add, which is
 * where the SDK normally stamps the _eraya_* line-item properties).
 */
(function () {
  'use strict';

  var PRICE_SELECTOR = '.price, .price__current, .price__was, .cart-item__price, '
    + '.product-card-container, .price-info';
  var applying = false;
  var observer = null;

  // The engine rewrites text nodes, which the observer would see as fresh
  // mutations. Draining the queue right after a rescan discards exactly the
  // records our own write produced — the rescan was page-wide, so anything
  // else batched alongside it has already been handled.
  function drainObserver() {
    if (observer) { try { observer.takeRecords(); } catch (_) {} }
  }

  function getManager() {
    try {
      var E = window.Eraya;
      if (E && E.sdk && typeof E.sdk.getPriceTestManager === 'function') {
        return E.sdk.getPriceTestManager();
      }
    } catch (_) {}
    return null;
  }

  function rescan() {
    if (applying) return;
    var mgr = getManager();
    if (!mgr || typeof mgr.updatePricesOnCurrentPage !== 'function') return;
    applying = true;
    try { tagAfterSellCards(mgr); } catch (_) {}
    try { mgr.updatePricesOnCurrentPage(); } catch (_) {}
    drainObserver();
    applying = false;
  }

  function rescanProduct(productId, variantId) {
    if (applying) return;
    var mgr = getManager();
    if (!mgr || typeof mgr.updatePricesForProduct !== 'function') return;
    applying = true;
    try { mgr.updatePricesForProduct(String(productId), String(variantId)); } catch (_) {}
    drainObserver();
    applying = false;
  }

  /* ------------------------------------------------------------------ *
   * 1. Variant change (Symmetry dispatches this ~10ms after the morph)
   * ------------------------------------------------------------------ */
  document.addEventListener('on:variant:change', function (event) {
    var variant = event.detail && event.detail.variant;
    if (!variant || !variant.id) return;

    // The re-rendered price node arrives from Liquid with the correct
    // data-variant-id already, but the product form and any sibling wrappers
    // that were not replaced can be stale — sync them before rescanning.
    var scope = (event.target && event.target.closest &&
      event.target.closest('.product-form, .js-product, .shopify-section')) || document;
    var priceEl = scope.querySelector('.product-info__price[data-product-id]');
    var productId = priceEl && priceEl.getAttribute('data-product-id');

    if (!productId) { rescan(); return; }

    scope.querySelectorAll('.product-info__price[data-product-id="' + productId + '"]')
      .forEach(function (el) { el.setAttribute('data-variant-id', String(variant.id)); });

    rescanProduct(productId, variant.id);
  });

  /* ------------------------------------------------------------------ *
   * 2/3. DOM injection: price morphs (sync) and card/cart injection (debounced)
   * ------------------------------------------------------------------ */
  function isPriceNode(n) {
    if (n.nodeType !== 1) return false;
    if (n.matches && n.matches(PRICE_SELECTOR)) return true;
    return typeof n.querySelector === 'function' && n.querySelector(PRICE_SELECTOR) !== null;
  }

  function hasProductId(n) {
    if (n.nodeType !== 1) return false;
    if (n.hasAttribute && n.hasAttribute('data-product-id')) return true;
    return typeof n.querySelector === 'function' && n.querySelector('[data-product-id]') !== null;
  }

  var timer = null;
  function schedule() { clearTimeout(timer); timer = setTimeout(rescan, 150); }

  if (typeof MutationObserver !== 'undefined') {
    // childList only — never attributes, or the engine's own writes re-trigger us.
    observer = new MutationObserver(function (mutations) {
      if (applying) return;
      var sync = false;
      var async = false;

      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (isPriceNode(node)) { sync = true; break; }
          if (!async && hasProductId(node)) async = true;
        }
        if (sync) break;
      }

      // A price node appeared (variant morph / quick buy). Re-apply NOW, in this
      // microtask, so the shopper never sees the original price paint.
      if (sync) { clearTimeout(timer); rescan(); return; }
      if (async) schedule();
    });

    var startObserving = function () {
      observer.observe(document.body, { childList: true, subtree: true });
    };
    if (document.body) startObserving();
    else document.addEventListener('DOMContentLoaded', startObserving);
  }

  /* ------------------------------------------------------------------ *
   * AfterSell PDP upsells (app block `aftersell/blocks/pdp-upsells`).
   *
   * AfterSell renders its cards client-side into #aftersell-pdp-upsell and
   * puts NO product id on them. But the app block inlines its whole config as
   * `window.pdpUpsellAppMetafields`, which maps every upsell product to its
   * variants:
   *
   *   upsell.products[] = { id: "10803879018823",
   *                         variants: [{ id: "53944052252999" }, ...] }
   *
   * That gives us variant -> product with no network call and no dependency on
   * the SDK exposing getProductIdFromVariantId. Cards look like:
   *
   *   .product-card                                   <- card root
   *     a[href="/products/<handle>"]
   *     .discounted-price-wrapper > h5.price-info     <- selling price
   *     select.select-dropdown
   *       option value="gid://shopify/ProductVariant/53944052252999"
   *
   * Resolution order: inlined metafield map -> SDK lookup -> /products.js.
   * The last one covers `upsell.type === "automatic"` blocks, whose products
   * are chosen at runtime and so are absent from the metafield map.
   *
   * NOTE: the <select> id is duplicated across cards (invalid HTML), so every
   * lookup is scoped to the card — never getElementById.
   * ------------------------------------------------------------------ */
  var AS_CONTAINER = '#aftersell-pdp-upsell, .product-card-container, [id*="aftersell"]';
  var AS_CARD = '.product-card';
  var AS_PRICE = '.price-info';
  var handleCache = Object.create(null);
  var asVariantMap = null;
  var shadowSeen = (typeof WeakSet !== 'undefined') ? new WeakSet() : null;
  var shadowObservers = [];

  function asShadowRoots() {
    var roots = [];
    var hosts = document.querySelectorAll(AS_CONTAINER);
    for (var i = 0; i < hosts.length; i++) {
      if (hosts[i].shadowRoot) roots.push(hosts[i].shadowRoot);
    }
    return roots;
  }

  function asCards() {
    var out = [];
    var roots = [document].concat(asShadowRoots());
    for (var i = 0; i < roots.length; i++) {
      var found = roots[i].querySelectorAll(AS_CARD);
      for (var j = 0; j < found.length; j++) out.push(found[j]);
    }
    return out;
  }

  // A MutationObserver on document.body is blind to mutations inside a shadow
  // root, and `change` events are composed:false so they never cross the
  // boundary either. Both have to be wired up on each root directly. There is
  // no event for "a shadow root was attached", hence the sweep further down.
  function ensureShadowHooks() {
    if (!shadowSeen) return;
    var roots = asShadowRoots();
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      if (shadowSeen.has(root)) continue;
      shadowSeen.add(root);

      if (typeof MutationObserver !== 'undefined') {
        var obs = new MutationObserver(function () {
          if (applying) return;
          applyAfterSellPrices();
        });
        obs.observe(root, { childList: true, subtree: true });
        shadowObservers.push(obs);
      }

      root.addEventListener('change', function (event) {
        var select = event.target;
        if (!select || select.nodeType !== 1 || select.tagName !== 'SELECT') return;
        applyAfterSellPrices();
      });
    }
  }

  function drainShadowObservers() {
    for (var i = 0; i < shadowObservers.length; i++) {
      try { shadowObservers[i].takeRecords(); } catch (_) {}
    }
  }

  function collectProducts(list, map) {
    if (!list || !list.length) return;
    for (var i = 0; i < list.length; i++) {
      var product = list[i];
      if (!product || !product.id || !product.variants) continue;
      for (var v = 0; v < product.variants.length; v++) {
        var variant = product.variants[v];
        if (variant && variant.id) map[String(variant.id)] = String(product.id);
      }
    }
  }

  function afterSellVariantMap() {
    if (asVariantMap) return asVariantMap;
    var map = Object.create(null);
    var metafields = window.pdpUpsellAppMetafields;
    if (metafields) {
      for (var key in metafields) {
        if (!Object.prototype.hasOwnProperty.call(metafields, key)) continue;
        var entry = metafields[key];
        if (!entry) continue;
        var upsell = entry.configuration && entry.configuration.upsell;
        if (upsell) collectProducts(upsell.products, map);
        if (entry.triggers) {
          for (var t = 0; t < entry.triggers.length; t++) {
            collectProducts(entry.triggers[t].products, map);
          }
        }
      }
    }
    asVariantMap = map;
    return map;
  }

  function productIdForVariant(mgr, variantId) {
    var productId = afterSellVariantMap()[String(variantId)] || null;
    if (!productId && mgr && typeof mgr.getProductIdFromVariantId === 'function') {
      try { productId = mgr.getProductIdFromVariantId(variantId); } catch (_) {}
    }
    return productId;
  }

  /* Shopify money formatting — window.pdpUpsellMoneyFormat is a standard
     money_format string, e.g. "€{{amount_with_comma_separator}}". */
  function withDelims(cents, precision, thousands, decimal) {
    var fixed = (cents / 100).toFixed(precision);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+$)/g, '$1' + thousands);
    return parts[1] ? whole + decimal + parts[1] : whole;
  }

  function formatMoney(cents, format) {
    var value = Number(cents);
    if (!isFinite(value)) return null;
    format = format || '{{amount}}';
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, name) {
      switch (name) {
        case 'amount_no_decimals': return withDelims(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelims(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelims(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelims(value, 2, "'", '.');
        default: return withDelims(value, 2, ',', '.');
      }
    });
  }

  // Test prices are stored as integer cents (11995). Accept major-unit strings
  // too, in case the SDK hands back "119.95" instead.
  function toCents(raw) {
    if (raw === null || raw === undefined || raw === '') return null;
    var str = String(raw).trim();
    if (/[.,]/.test(str)) {
      var major = parseFloat(str.replace(',', '.'));
      return isFinite(major) ? Math.round(major * 100) : null;
    }
    var int = parseInt(str, 10);
    return isFinite(int) ? int : null;
  }

  /* Resolve the price this visitor should see for a variant, preferring the
     test's own priceVariations (which also carry compareAtPrice) and falling
     back to the _eraya_test_price the cart transform reads. */
  function testPricingFor(mgr, productId, variantId) {
    if (!mgr) return null;
    var properties = null;
    try {
      if (typeof mgr.getSimpleCartProperties === 'function') {
        properties = mgr.getSimpleCartProperties(String(productId), String(variantId));
      }
    } catch (_) {}

    var result = null;
    try {
      var testId = typeof mgr.getTestForProduct === 'function'
        ? mgr.getTestForProduct(String(productId)) : null;
      var test = testId && typeof mgr.getTest === 'function' ? mgr.getTest(testId) : null;
      if (test && test.status === 'active' && test.priceVariations) {
        var variationId = properties && properties._eraya_variation;
        for (var i = 0; i < test.priceVariations.length; i++) {
          var pv = test.priceVariations[i];
          if (String(pv.variantId) !== String(variantId)) continue;
          if (variationId && String(pv.variationId) !== String(variationId)) continue;
          result = { price: toCents(pv.price), compareAt: toCents(pv.compareAtPrice) };
          break;
        }
      }
    } catch (_) {}

    if (!result && properties && properties._eraya_test_price) {
      result = { price: toCents(properties._eraya_test_price), compareAt: null };
    }
    return result && result.price ? result : null;
  }

  function writePrice(el, cents) {
    if (!el || !cents) return;
    var text = formatMoney(cents, window.pdpUpsellMoneyFormat);
    if (!text || el.textContent.trim() === text) return;
    el.textContent = text;
  }

  /* The engine cannot reach shadow DOM (its scan is a plain
     document.querySelectorAll), so for shadow-rendered cards the bridge does
     the rewrite itself rather than just tagging them for the engine. */
  function applyAfterSellPrices() {
    var mgr = getManager();
    if (!mgr) return;
    var cards = asCards();
    if (!cards.length) return;

    // Save/restore rather than assign: rescan() already holds this flag when
    // it calls through here, and clearing it early would let the observer
    // re-enter mid-pass.
    var wasApplying = applying;
    applying = true;
    try {
      for (var i = 0; i < cards.length; i++) {
        var card = cards[i];
        card.classList.add('eraya-upsell-card');

        var select = card.querySelector('select');
        var variantId = select ? normalizeId(select.value) : null;
        var productId = variantId ? productIdForVariant(mgr, variantId) : null;

        if (!productId) {
          card.removeAttribute('data-product-id');
          card.removeAttribute('data-variant-id');
          if (variantId) resolveByHandle(card, mgr, variantId);
          continue;
        }

        card.setAttribute('data-product-id', String(productId));
        card.setAttribute('data-variant-id', String(variantId));

        var prices = card.querySelectorAll(AS_PRICE);
        for (var p = 0; p < prices.length; p++) {
          prices[p].classList.add(
            prices[p].closest('.discounted-price-wrapper') ? 'eraya-price' : 'eraya-price-compare'
          );
        }

        // Light-DOM cards are the engine's job; only rewrite what it can't see.
        if (card.getRootNode && card.getRootNode() === document) continue;

        var pricing = testPricingFor(mgr, productId, variantId);
        if (!pricing) continue;

        writePrice(card.querySelector('.discounted-price-wrapper ' + AS_PRICE), pricing.price);
        if (pricing.compareAt && pricing.compareAt > pricing.price) {
          var compareEl = card.querySelector('.eraya-price-compare');
          if (compareEl) writePrice(compareEl, pricing.compareAt);
        }
      }
    } catch (_) {}
    drainShadowObservers();
    applying = wasApplying;
  }

  // Kept for the light-DOM path: stamp ids so the engine can match the card.
  function tagAfterSellCards() {
    ensureShadowHooks();
    applyAfterSellPrices();
  }

  // Resolve `automatic` upsells that are absent from the metafield map.
  function resolveByHandle(card, mgr, preferredVariantId) {
    var link = card.querySelector('a[href*="/products/"]');
    if (!link) return;
    var match = /\/products\/([^/?#]+)/.exec(link.getAttribute('href') || '');
    if (!match) return;
    var handle = match[1];

    if (handleCache[handle] === 'pending' || handleCache[handle] === null) return;
    if (handleCache[handle]) {
      afterSellVariantMap()[String(preferredVariantId)] = String(handleCache[handle].id);
      return;
    }

    handleCache[handle] = 'pending';
    fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        handleCache[handle] = data || null;
        if (!data) return;
        var map = afterSellVariantMap();
        for (var i = 0; i < (data.variants || []).length; i++) {
          map[String(data.variants[i].id)] = String(data.id);
        }
        applyAfterSellPrices();
      })
      .catch(function () { handleCache[handle] = null; });
  }

  /* AfterSell attaches its shadow root asynchronously and there is no event
     for it, so sweep for a while after load, then stop. Once the hooks are on,
     the shadow observer handles every later re-render. */
  var sweeps = 0;
  var sweepTimer = setInterval(function () {
    ensureShadowHooks();
    applyAfterSellPrices();
    if (++sweeps > 60) clearInterval(sweepTimer); // ~30s at 500ms
  }, 500);

  /* ------------------------------------------------------------------ *
   * Buy It Now / accelerated checkout — charge the test price.
   *
   * These bypass /cart/add, so the SDK's fetch/XHR patch never stamps the
   * _eraya_* properties the cart-transform Function reads. Intercept in the
   * capture phase, and ONLY when the SDK confirms this exact variant is under
   * test — otherwise the native express checkout runs untouched.
   * ------------------------------------------------------------------ */
  var BUY_NOW_SELECTOR = 'shopify-buy-it-now-button, shopify-accelerated-checkout, ' +
    '[data-shopify="payment-button"], .shopify-payment-button, .shopify-payment-button__button';

  function findBuyNowTarget(event) {
    var path = (typeof event.composedPath === 'function' && event.composedPath()) || [event.target];
    for (var i = 0; i < path.length; i++) {
      var el = path[i];
      if (el && el.nodeType === 1 && el.matches && el.matches(BUY_NOW_SELECTOR)) return el;
    }
    return null;
  }

  function findProductForm(el, event) {
    var form = el.closest && el.closest('form[action*="/cart/add"]');
    if (form) return form;

    var formId = el.getAttribute && el.getAttribute('form');
    if (formId) {
      form = document.getElementById(formId);
      if (form) return form;
    }

    var path = (typeof event.composedPath === 'function' && event.composedPath()) || [];
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (node && node.nodeType === 1 && node.matches && node.matches('form[action*="/cart/add"]')) {
        return node;
      }
      if (node && node.nodeType === 1 && node.closest) {
        var found = node.closest('form[action*="/cart/add"]');
        if (found) return found;
      }
    }

    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    return forms.length === 1 ? forms[0] : null;
  }

  function normalizeId(value) {
    if (!value) return null;
    var str = String(value);
    var idx = str.lastIndexOf('/');
    if (idx !== -1) str = str.slice(idx + 1);
    return /^\d+$/.test(str) ? str : null;
  }

  document.addEventListener('click', function (event) {
    var button = findBuyNowTarget(event);
    if (!button) return;

    var mgr = getManager();
    if (!mgr || typeof mgr.getSimpleCartProperties !== 'function') return;

    var form, variantId, quantity, productId, properties;
    try {
      form = findProductForm(button, event);
      if (!form) return;

      var idInput = form.querySelector('[name="id"]');
      variantId = normalizeId(idInput && idInput.value);
      // Fail closed: no known exact variant means no stamp, and no interception.
      if (!variantId) return;

      var qtyInput = form.querySelector('[name="quantity"]');
      quantity = parseInt((qtyInput && qtyInput.value) || '1', 10);
      if (!quantity || quantity < 1) quantity = 1;

      productId = typeof mgr.getProductIdFromVariantId === 'function'
        ? mgr.getProductIdFromVariantId(variantId)
        : null;
      if (!productId) {
        var owner = form.closest('[data-product-id]') ||
          document.querySelector('.product-info__price[data-product-id]');
        productId = owner && owner.getAttribute('data-product-id');
      }
      if (!productId) return;

      properties = mgr.getSimpleCartProperties(String(productId), String(variantId));
    } catch (_) {
      return;
    }

    // Not a test purchase — let the native express checkout run.
    if (!properties || !properties._eraya_test_price) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    var done = function () { window.location.href = '/checkout'; };

    try {
      if (typeof mgr.addToCartWithTestData === 'function') {
        Promise.resolve(mgr.addToCartWithTestData(variantId, quantity)).then(done, done);
        return;
      }
    } catch (_) {}

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: quantity, properties: properties }] })
    }).then(done, done);
  }, true);

  /* ------------------------------------------------------------------ *
   * Initial pass.
   *
   * The SDK bundle loads asynchronously from the app-extension CDN, and so
   * does AfterSell. Whichever loses that race, every rescan path above bails
   * on a null manager and nothing ever retries — so theme-native prices come
   * out right (the SDK scans those itself at init) while AfterSell cards keep
   * their original price forever. Poll until the SDK exists, then run the
   * first tag-and-apply ourselves.
   * ------------------------------------------------------------------ */
  var readyTries = 0;
  (function waitForManager() {
    if (getManager()) { rescan(); return; }
    if (readyTries++ > 150) return; // ~30s, then give up quietly
    setTimeout(waitForManager, 200);
  })();

  /* Diagnostic: run window.__erayaDebug() in the console. */
  window.__erayaDebug = function () {
    var mgr = getManager();
    ensureShadowHooks();
    var cards = asCards();
    var host = document.getElementById('aftersell-pdp-upsell');
    var report = {
      bridgeVersion: 'aftersell-4-shadow',
      sdkLoaded: !!(window.Eraya && window.Eraya.sdk),
      managerFound: !!mgr,
      afterSellUsesShadowDom: !!(host && host.shadowRoot),
      shadowRootsHooked: asShadowRoots().length,
      variantMapSize: Object.keys(afterSellVariantMap()).length,
      moneyFormat: window.pdpUpsellMoneyFormat,
      afterSellCards: cards.length,
      cards: []
    };
    for (var i = 0; i < cards.length; i++) {
      var c = cards[i];
      var sel = c.querySelector('select');
      var vid = sel ? normalizeId(sel.value) : null;
      var pid = vid ? productIdForVariant(mgr, vid) : null;
      var props = null;
      try {
        if (mgr && pid && typeof mgr.getSimpleCartProperties === 'function') {
          props = mgr.getSimpleCartProperties(String(pid), String(vid));
        }
      } catch (e) { props = { error: String(e) }; }
      var pricing = pid ? testPricingFor(mgr, pid, vid) : null;
      report.cards.push({
        variantId: vid,
        productId: pid,
        inShadowDom: !!(c.getRootNode && c.getRootNode() !== document),
        rawCartProperties: props,
        resolvedPriceCents: pricing && pricing.price,
        resolvedCompareCents: pricing && pricing.compareAt,
        wouldRender: pricing ? formatMoney(pricing.price, window.pdpUpsellMoneyFormat) : null,
        priceTextNow: (c.querySelector(AS_PRICE) || {}).textContent
      });
    }
    console.log(JSON.stringify(report, null, 2));
    return report;
  };
})();
